import {v4 as uuidv4} from 'uuid';
import api from '@/api';
import {jsonApiRawMutate} from '@/utils/jsonapiClient';

type ChildBase = {
  kind: 'photo' | 'video';
  note?: string | null;
  weight: number;
};

// New frames must include the uploaded file. Existing frames may omit sourceUrl
// so a display/thumbnail URL is not sent as a new source_url.
export type ChildPayload =
  | (ChildBase & {sourceUrl: string})
  | (ChildBase & {id: string; sourceUrl?: string});
export interface CreateSubmissionPayload {
  kind: 'photo' | 'video' | 'blog' | 'mixed_media';
  deliverableKind?: string | null;
  sourceUrl?: string;
  note?: string | null;
  childContentSubmissions?: ChildPayload[];
}
export interface ReviseSubmissionPayload {
  sourceUrl?: string;
  note?: string | null;
  childContentSubmissions?: ChildPayload[];
}

// The `create` endpoint only accepts mixed-media children as JSON:API `relationships` +
// `included` resources (Ember's shape) — the backend resolves those into permitted child
// hashes. Sent as a nested attribute, `create` rejects them with ForbiddenAttributesError
// (only `update`/revise re-permits that attribute). jsonapi-react can't emit `included`, so
// the document is built by hand. Parent type is plural, child type singular — matching the
// Ember serializer the API is written against.
const PARENT_TYPE = 'content_submissions';
const CHILD_TYPE = 'content_submission';

function childAttributes(child: ChildPayload) {
  const attributes: Record<string, unknown> = {
    kind: child.kind,
    note: child.note ?? null,
    weight: child.weight,
  };
  if (child.sourceUrl !== undefined) {
    attributes.source_url = child.sourceUrl;
  }
  return attributes;
}

function buildMixedMediaDocument(
  attributes: Record<string, unknown>,
  children: ChildPayload[],
  parentId?: string
) {
  const included = children.map((child) => ({
    type: CHILD_TYPE,
    id: 'id' in child ? child.id : uuidv4(),
    attributes: childAttributes(child),
  }));

  return {
    data: {
      type: PARENT_TYPE,
      id: parentId ?? uuidv4(),
      attributes,
      relationships: {
        child_content_submissions: {
          data: included.map(({type, id}) => ({type, id})),
        },
      },
    },
    included,
  };
}

const contentSubmissionUpdaters = {
  create: (gigId: string, payload: CreateSubmissionPayload) => {
    if (payload.childContentSubmissions?.length) {
      return jsonApiRawMutate(
        `/paid_gigs/${gigId}/content_submissions`,
        buildMixedMediaDocument(
          {
            kind: payload.kind,
            deliverable_kind: payload.deliverableKind ?? null,
          },
          payload.childContentSubmissions
        )
      );
    }
    return api.post(`/paid_gigs/${gigId}/content_submissions`, payload);
  },
  revise: (gigId: string, id: string, payload: ReviseSubmissionPayload) => {
    if (payload.childContentSubmissions) {
      // Same JSON:API document as create. jsonapi-react mutate cannot emit
      // `included` and types this nested URL as `paid_gigs`, which would drop
      // child IDs the backend needs to version frames instead of destroying them.
      return jsonApiRawMutate(
        `/paid_gigs/${gigId}/content_submissions/${id}`,
        buildMixedMediaDocument({}, payload.childContentSubmissions, id),
        'PUT'
      );
    }
    return api.put(`/paid_gigs/${gigId}/content_submissions/${id}`, payload);
  },
  archive: (id: string) => api.put(`/content_submissions/${id}/archive`, {}),
};

export default contentSubmissionUpdaters;
